import { Injectable } from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';
import {
    Announcement,
    AnnouncementFormInt,
    AnnouncementInt
} from '../../components/dashboard/admin/interfaces/announcement';
import {DateUtil} from '../../components/dashboard/date-util';
import {PaginationObject} from '../../components/dashboard/pagination-object';
import {environment} from '../../../environments/environment';

@Injectable()
export class AnnouncementService {

    private announcementTable = new BehaviorSubject<Announcement[]>(null);
    currentAnnouncementTable = this.announcementTable.asObservable();

    private announcementPagination = new BehaviorSubject<PaginationObject>(null);
    currentAnnouncementPagination = this.announcementPagination.asObservable();

    private announcementStatus = new BehaviorSubject<string>(null);
    currentAnnouncementStatus = this.announcementStatus.asObservable();

    private urlPrefix = environment.is_local ? 'http://localhost:8081' : '';

    private readonly httpOptions = {
        observe: 'response',
        responseType: 'text',
        withCredentials: true
    } as const;

    constructor(private readonly http: HttpClient) {
        this.announcementPagination.next(this.buildPagination(5));
        this.announcementStatus.next('');
    }

    extractData(res: any) {
        return res;
    }

    createAnnouncement(model: Announcement): Observable<HttpResponse<any>> {
        return this.http.post(this.urlPrefix + `/dashboard/announcement`, model, this.httpOptions).pipe(map(this.extractData));
    }

    updateAnnouncement(model: Announcement): Observable<HttpResponse<any>> {
        return this.http.put(this.urlPrefix + `/dashboard/announcement`, model, this.httpOptions).pipe(map(this.extractData));
    }

    getAnnouncement(id: string) {
        return this.http.get(this.urlPrefix + `/dashboard/announcement/${id}`).pipe(map(this.extractData))
    }

    getAnnouncements(): Observable<any[]> {
        return this.http.get<any[]>(this.urlPrefix + `/dashboard/announcement/announcements/alllabs`).pipe(map(this.extractData));
    }

    getActiveAnnouncements(): Observable<any[]> {
        return this.http.get<any[]>(this.urlPrefix + `/dashboard/announcement/announcements/activeAnnouncements`).pipe(map(this.extractData));
    }

    getAnnouncementsPaginated(): Observable<any[]> {
        const pc: PaginationObject = this.announcementPagination.getValue();
        const status: string = this.announcementStatus.getValue();

        return this.http.get<any[]>(this.urlPrefix + `/dashboard/announcement/announcements/alllabs/admin`,
            {
                params: new HttpParams()
                    .set('page', pc.currentPage.toString())
                    .set('results-per-page', pc.pageSize.toString())
                    .set('status', status)
            }).pipe(map(this.extractData));
    }

    getReportingCenters(id: string): Observable<Array<AnnouncementInt[]>> {
        return this.http.get<Array<AnnouncementInt[]>>(this.urlPrefix + `/dashboard/announcement/centers/${id}`).pipe(map(this.extractData));
    }

    getAnnouncementCount(): Observable<number> {
        const status: string = this.announcementStatus.getValue();

        return this.http.get<number>(this.urlPrefix + `/dashboard/announcement/announcements/alllabs/admin/count`,
            {
                params: new HttpParams()
                    .set('status', status)
            }).pipe(map(this.extractData));
    }

    createMappingToReportingCenter(announcementId: string, reportingCenter: string): Observable<HttpResponse<any>> {
        return this.http.post(this.urlPrefix + `/dashboard/announcement/centers/${announcementId}/${reportingCenter}`, null, this.httpOptions).pipe(map(this.extractData));
    }

    deleteMappingToReportingCenter(announcementId: string, reportingCenter: string): Observable<HttpResponse<any>> {
        return this.http.delete(this.urlPrefix + `/dashboard/announcement/centers/${announcementId}/${reportingCenter}`, this.httpOptions).pipe(map(this.extractData));
    }

    updateMappingToReportingCenter(announcementId: string, newReportingCenters: Array<string>): Observable<HttpResponse<any>> {
        return this.http.put(this.urlPrefix + `/dashboard/announcement/centers/${announcementId}`, newReportingCenters, this.httpOptions).pipe(map(this.extractData));
    }

    modelPreparing(model, initial?) {
        return {
            announcement: {
                announcementId: model.announcementId,
                announcementTypeCde: model.announcementType.announcementTypeCde,
                announcementTitle: model.announcementTitle,
                announcementTxt: model.announcementTxt,
                effectiveDate: DateUtil.formatDate(model.effectiveDate),
                effectiveDateTime: DateUtil.formatTime(model.effectiveDate),
                endDate: DateUtil.formatDate(model.endDate),
                endDateTime: DateUtil.formatTime(model.endDate),
                linkTxt: model.linkTxt,
                linkUrl: model.linkUrl,
                releaseNumber: model.releaseNumber
            },
            reportingCenters: model.reportingCenters,
        };
    }

    prepareTime(model: AnnouncementFormInt) {
        const defaultTime = '00:00';
        const secondsPlusTimezone = ':00.000-06:00';
        const effectiveTime = model.announcement.effectiveDateTime != null ? model.announcement.effectiveDateTime : defaultTime;
        const endTime = model.announcement.endDateTime != null ? model.announcement.endDateTime : defaultTime;
        const effective = `${DateUtil.formatDateISO(model.announcement.effectiveDate)}T${effectiveTime}${secondsPlusTimezone}`;
        const end = `${DateUtil.formatDateISO(model.announcement.endDate)}T${endTime}${secondsPlusTimezone}`;
        model.announcement.effectiveDate = effective;
        model.announcement.endDate = end;
    }

    changeSamplePagination(message: PaginationObject) {
        this.announcementPagination.next(message);
    }

    updatePagination(totalCount: number) {
        const pc: PaginationObject = this.announcementPagination.getValue();
        pc.totalCount = totalCount;
        this.changeSamplePagination(pc);
    }

    updateStatus(status: string) {
        this.changeStatus(status);
    }

    changeStatus(message: string) {
        this.announcementStatus.next(message);
    }

    buildPagination(pageSize: number) {
        const pagination = new PaginationObject();
        pagination.currentPage = 0;
        pagination.pageSize = pageSize;
        return pagination;
    }

    toRecordObjectList(jsonFileRecordList: any): Announcement[] {
        const fileRecordObjectList: Announcement[] = new Array<Announcement>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }

    changeMessage(message: Announcement[]) {
        this.announcementTable.next(message);
    }
}
